import React from 'react';
import { useNavigate } from 'react-router-dom';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/system/Box';
import Tooltip from '@mui/material/Tooltip';
import ListItemIcon from '@mui/material/ListItemIcon';
import IconButton from '@mui/material/IconButton';

import CloseIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';

import PAGES from '../pageTree';
import { Layout } from 'src/styles';
import { hasPermissions } from 'src/utils/hasPermission';
import { useAppSelector } from 'src/app/hooks';

import { AsmubipDrawer } from 'src/styles/styledComponents';
import Divider from '@mui/material/Divider';

type SidebarMenuProps = {
  handleDrawerClose: () => void;
  handleDrawerToggle: () => void;
  open: boolean;
};

export default function SidebarMenu({
  handleDrawerClose,
  handleDrawerToggle,
  open,
}: SidebarMenuProps) {
  const { isMobile, key, perfil } = useAppSelector((state) => state.page);
  const navigate = useNavigate();

  return (
    <AsmubipDrawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={handleDrawerClose}
      ModalProps={{
        keepMounted: true,
      }}>
      <Box>
        <Box sx={{ p: 1 }}>
          <IconButton onClick={handleDrawerToggle}>
            {open ? <CloseIcon color="drawer" /> : <MenuIcon color="drawer" />}
          </IconButton>
        </Box>
        <List>
          {Object.entries(PAGES)
            .filter(
              ([PAGE_KEY, PAGE]) =>
                !PAGE.skipMenu && hasPermissions({ perfil, permissions: PAGE_KEY })
            )
            .map(([PAGE_KEY, PAGE]) => (
              <React.Fragment key={PAGE_KEY}>
                {PAGE.dividerUp && (
                  <Divider component="li" variant="middle" sx={Layout.sideBarDivider} />
                )}
                <Tooltip title={PAGE.title}>
                  <ListItemButton
                    {...(PAGE.externalUrl
                      ? {
                          href:
                            typeof PAGE.externalUrl === 'string'
                              ? PAGE.externalUrl
                              : PAGE.externalUrl(true),
                        }
                      : {})}
                    selected={PAGE_KEY === 'home' ? key === '' : key === PAGE_KEY}
                    onClick={() => {
                      handleDrawerClose();
                      if (PAGE.onClick) {
                        PAGE.onClick();
                        return;
                      }
                      if (!PAGE.externalUrl) {
                        navigate(PAGE_KEY === 'home' ? '/' : PAGE_KEY);
                      }
                    }}>
                    {PAGE.Icon && (
                      <ListItemIcon sx={Layout.sideBarItemIcon}>
                        <PAGE.Icon color="drawer" />
                      </ListItemIcon>
                    )}
                    <ListItemText primary={PAGE.title} sx={Layout.sideBarItem} />
                  </ListItemButton>
                </Tooltip>
              </React.Fragment>
            ))}
        </List>
      </Box>
      <List>
        <ListItemButton
          {...(PAGES.signout.externalUrl
            ? {
                href:
                  typeof PAGES.signout.externalUrl === 'string'
                    ? PAGES.signout.externalUrl
                    : PAGES.signout.externalUrl(true),
              }
            : {})}
          selected={key === 'signout'}
          onClick={() => {
            handleDrawerClose();
            if (PAGES.signout.onClick) {
              PAGES.signout.onClick();
              return;
            }
            if (!PAGES.signout.externalUrl) {
              navigate('signout');
            }
          }}>
          {PAGES.signout.Icon && (
            <ListItemIcon sx={Layout.sideBarItemIcon}>
              <PAGES.signout.Icon color="drawer" />
            </ListItemIcon>
          )}
          <ListItemText primary={PAGES.signout.title} sx={Layout.sideBarItem} />
        </ListItemButton>
      </List>
    </AsmubipDrawer>
  );
}
